import { useEffect, useRef } from "react";
import { WarningIcon } from "./icons";

interface SessionExpiredDialogProps {
  /** [로그인 페이지로 이동]. 라우터 가드가 실제 이동을 맡는다. */
  onGoToLogin: () => void;
  /** 안내만 닫는다. 편집하던 내용을 복사해 두려는 경우를 위해 남겨 둔다. */
  onDismiss: () => void;
}

/**
 * 세션이 끊겼음을 알리는 모달.
 *
 * 이 상황에서 가장 나쁜 결말은 **아무 말 없이 로그인 화면으로 튕기는 것**이다.
 * 편집하던 화면이 사라진 이유를 알 수 없고, 저장되지 않은 변경이 있었다는
 * 사실조차 모른 채 넘어간다. 그래서 먼저 알리고, 이동은 사용자가 누른다.
 *
 * **`Modal` 을 쓰지 않는다.** 저쪽은 [취소]/[제출] 짝을 가진 폼 상자다.
 * 여기서 필요한 것은 되돌릴 것이 없는 안내와 한 방향의 행동이다.
 *
 * 닫기를 남겨 둔 이유: 세션이 끊겨도 **화면의 내용은 그대로 살아 있다.**
 * 다른 탭에서 다시 로그인하면 쿠키가 공유되므로, 이 창으로 돌아와 [저장] 을
 * 누르면 작업을 잃지 않는다. 그 길을 막지 않는다.
 */
export function SessionExpiredDialog({
  onGoToLogin,
  onDismiss,
}: SessionExpiredDialogProps) {
  const primaryRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    primaryRef.current?.focus();

    // Esc 는 닫기와 같다. 이동은 사용자가 명시적으로 눌러야 한다 —
    // 키 하나로 편집 화면을 떠나게 만들지 않는다.
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onDismiss();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onDismiss]);

  return (
    <div
      className="modal-backdrop modal-backdrop--top"
      role="alertdialog"
      aria-modal
      aria-labelledby="session-expired-title"
    >
      <div className="modal">
        <header className="modal__header">
          <h2 className="row-inline" id="session-expired-title">
            <WarningIcon size={18} />
            로그인이 만료되었습니다
          </h2>
        </header>

        <div className="modal__content">
          <p>
            일정 시간이 지나 로그인이 풀렸습니다. 다시 로그인해야 저장·배포를
            비롯한 모든 기능을 쓸 수 있습니다.
          </p>
          <p className="warning">
            저장되지 않은 변경은 아직 서버에 반영되지 않았습니다.
          </p>
          <p className="field-hint">
            편집하던 내용을 지키려면 이 창을 닫고{" "}
            <strong>다른 탭에서 다시 로그인</strong>한 뒤 돌아와 저장하세요.
            로그인 정보는 탭 사이에 공유됩니다.
          </p>
        </div>

        <footer className="row modal__actions">
          <button type="button" onClick={onDismiss}>
            닫기
          </button>
          <button
            ref={primaryRef}
            type="button"
            className="primary"
            onClick={onGoToLogin}
          >
            로그인 페이지로 이동
          </button>
        </footer>
      </div>
    </div>
  );
}
